import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { NotificationsService, AppNotification } from '../../core/notifications.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatDividerModule],
  template: `
    <div class="panel">
      <div class="panel-header">
        <h3>Notifications</h3>
        <button mat-button class="mark-all" (click)="markAllRead()" *ngIf="(svc.notifications$ | async)?.length">
          Mark all read
        </button>
      </div>
      <mat-divider></mat-divider>
      <div class="panel-body">
        <div *ngFor="let n of svc.notifications$ | async"
          class="notif-item" [class.unread]="!n.read"
          (click)="onItemClick(n)">
          <div class="notif-icon" [ngClass]="n.type">
            <mat-icon>{{ iconFor(n.type) }}</mat-icon>
          </div>
          <div class="notif-content">
            <p class="notif-title">{{ n.title }}</p>
            <p class="notif-body">{{ n.body }}</p>
            <p class="notif-time">{{ timeAgo(n.createdAt) }}</p>
          </div>
          <span class="unread-dot" *ngIf="!n.read"></span>
        </div>
        <div class="empty" *ngIf="(svc.notifications$ | async)?.length === 0">
          <mat-icon>notifications_none</mat-icon>
          <p>No notifications yet</p>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .panel {
      width: 380px;
      max-height: 480px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 12px 40px rgba(15,23,42,0.16);
      border: 1px solid #E2E8F0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 18px 10px;
    }
    .panel-header h3 { margin: 0; font-size: 15px; font-weight: 700; color: #0F172A; }
    .mark-all { font-size: 12px; color: #0D9488; }
    .panel-body { overflow-y: auto; flex: 1; }

    .notif-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 14px 18px;
      cursor: pointer;
      transition: background 0.12s;
      position: relative;
    }
    .notif-item:hover { background: #F8FAFC; }
    .notif-item.unread { background: #F0F7FF; }
    .notif-item.unread:hover { background: #E8F2FF; }

    .notif-icon {
      width: 36px; height: 36px;
      border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .notif-icon mat-icon { font-size: 18px; width: 18px; height: 18px; }
    .notif-icon.emergency_absence { background: #FEE2E2; }
    .notif-icon.emergency_absence mat-icon { color: #DC2626; }
    .notif-icon.vacation_approved { background: #DBEAFE; }
    .notif-icon.vacation_approved mat-icon { color: #0D9488; }
    .notif-icon.shift_needs_coverage { background: #FEF3C7; }
    .notif-icon.shift_needs_coverage mat-icon { color: #D97706; }
    .notif-icon.general { background: #F1F5F9; }
    .notif-icon.general mat-icon { color: #64748B; }

    .notif-content { flex: 1; min-width: 0; }
    .notif-title { margin: 0 0 2px; font-size: 13px; font-weight: 600; color: #0F172A; }
    .notif-body { margin: 0 0 4px; font-size: 12px; color: #64748B; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .notif-time { margin: 0; font-size: 11px; color: #94A3B8; }

    .unread-dot {
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #0D9488;
      flex-shrink: 0;
      margin-top: 6px;
    }

    .empty {
      text-align: center;
      padding: 40px 20px;
      color: #94A3B8;
    }
    .empty mat-icon { font-size: 40px; width: 40px; height: 40px; opacity: 0.3; }
    .empty p { margin: 8px 0 0; font-size: 13px; }
  `]
})
export class NotificationPanelComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();

  constructor(public svc: NotificationsService, private router: Router) {}

  ngOnInit(): void {
    this.svc.loadNotifications();
  }

  iconFor(type: string): string {
    switch (type) {
      case 'emergency_absence': return 'warning';
      case 'vacation_approved': return 'event_available';
      case 'shift_needs_coverage': return 'swap_horiz';
      default: return 'notifications';
    }
  }

  timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  onItemClick(n: AppNotification): void {
    if (!n.read) this.svc.markRead(n.id);
    this.closed.emit();
    if (n.referenceType === 'absence_request') {
      this.router.navigate(['/employee-absences']);
    } else if (n.referenceType === 'schedule_entry') {
      this.router.navigate(['/schedules/monitor']);
    }
  }

  markAllRead(): void {
    this.svc.markAllRead();
  }
}
